import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { SnackBarService } from 'src/app/services/snack-bar.service';
import { DatePipe } from '@angular/common';
import { BespCofmListarControleFinancaMovimento } from '@app/models/BespCofmListarControleFinancaMovimento';
import { debounceTime } from 'rxjs/operators';
import { ListaDadosColuna } from 'src/app/components/lista-dados/lista-dados.component';

@Component({
  selector: 'app-controle-financa-movimentos-filtro',
  templateUrl: './list-controle-financa-movimentos-filtro.component.html',
  styleUrls: ['./list-controle-financa-movimentos-filtro.component.css']
})
export class ListControleFinancaMovimentosFiltroComponent implements OnInit, OnDestroy {
  public movimentos: BespCofmListarControleFinancaMovimento[] = [];
  public colunas: ListaDadosColuna[] = [
    { campo: 'noMovimentacao', rotulo: 'Descrição', tamanho: '300px' },
    { campo: 'vlMovimentacao', rotulo: 'Valor', tamanho: '92px', formatador: (_, valor) => this.formatarMoeda(valor) },
    { campo: 'dtMovimentacao', rotulo: 'Data', tamanho: '90px', formatador: (_, valor) => this.formatarData(valor) },
    { campo: 'dtVencimento', rotulo: 'Vencimento', tamanho: '90px', formatador: (row, valor) => row.noTipoMovimento === 'Saída' ? this.formatarData(valor) : '' },
    { campo: 'noTipoMovimento', rotulo: 'Tipo Movimento', tamanho: '110px' },
    { campo: 'dtControle', rotulo: 'Data Referência', tamanho: '100px', formatador: (_, valor) => this.formatarMesAno(valor) }
  ];
  public subscription?: Subscription;

  public filterNoMovimentacao = new FormControl<string>("");
  public filterVlMovimentacao = new FormControl<string>("");
  public filterDtMovimentacaoIni = new FormControl<string>("");
  public filterDtMovimentacaoFim = new FormControl<string>("");
  public filterDtVencimentoIni = new FormControl<string>("");
  public filterDtVencimentoFim = new FormControl<string>("");
  public filterNoTipoMovimento = new FormControl<string>("");

  public filterDtMovimentacao = new FormGroup({
    dtIni: this.filterDtMovimentacaoIni,
    dtFim: this.filterDtMovimentacaoFim,
  });

  public filterDtVencimento = new FormGroup({
    dtIni: this.filterDtVencimentoIni,
    dtFim: this.filterDtVencimentoFim,
  });

  public filterFormGroup: FormGroup = new FormGroup({
    noMovimentacao: this.filterNoMovimentacao,
    vlMovimentacao: this.filterVlMovimentacao,
    dtMovimentacao: this.filterDtMovimentacao,
    dtVencimento: this.filterDtVencimento,
    noTipoMovimento: this.filterNoTipoMovimento,
  });

  public constructor(
    private httpClient: HttpClient,
    private router: Router,
    private route: ActivatedRoute,
    private datePipe: DatePipe,
    private snackBarService: SnackBarService,
  ) {}

  public ngOnInit(): void {
    const match = document.cookie.match(/(^| )controleFinancaFiltros=([^;]+)/);
    if (match) {
      try {
        const filtros = JSON.parse(decodeURIComponent(match[2]));
        this.filterFormGroup.patchValue(filtros);
        const match2 = document.cookie.match(/(^| )controleFinancaFiltrado=([^;]+)/);
        if (match2) {
          const isFiltrado = match2[2] === "true";
          if (isFiltrado) {
            this.onFilter();
          }
        }
      } catch {
        this.snackBarService.exibirErro("Erro ao carregar filtros do cookie.");
      }
    }

    this.filterFormGroup.valueChanges
      .pipe(debounceTime(1000))
      .subscribe(val => {
        if (!this.isFiltroVazio()) {
          document.cookie = "controleFinancaFiltros=" + encodeURIComponent(JSON.stringify(val)) + ";path=/;max-age=604800";
        }
        else {
          this.filterFormGroup.reset();
          document.cookie = "controleFinancaFiltros=;path=/;max-age=0";
        }
      });

    setTimeout(() => {
      const input = document.querySelector(
        '.filter-content mat-form-field:first-child input[matInput]'
      ) as HTMLInputElement;
      if (input) {
        input.focus();
      }
    });
  }

  public onFilter() {
    if (!this.isFiltroVazio()) {
      document.cookie = "controleFinancaFiltros=" + encodeURIComponent(JSON.stringify(this.filterFormGroup.value)) + ";path=/;max-age=604800";
      document.cookie = "controleFinancaFiltrado=true;path=/;max-age=604800";
    }
    this.subscription = this.httpClient.post<BespCofmListarControleFinancaMovimento[]>(
      `/api/ControleFinancaMovimentos/ListarMovimento/`, this.filterFormGroup.value)
      .subscribe({
        next: (res) => {
          this.unsubscribe();
          this.onResult(res);
        },
        error: (err) => {
          this.unsubscribe();
          this.snackBarService.exibirErro(`Erro! - ${err.status} - ${err.statusText}`);
        }
      });
  }

  public ngOnDestroy(): void {
    this.unsubscribe();
  }

  public onEdit(controleFinanca: BespCofmListarControleFinancaMovimento) {
    this.router.navigate(['/controle-financa', controleFinanca.idControleFinanca], { relativeTo: this.route });
  }

  public onClear() {
    this.filterFormGroup.reset();
    this.movimentos = [];
    document.cookie = "controleFinancaFiltros=;path=/;max-age=0";
    document.cookie = "controleFinancaFiltrado=;path=/;max-age=0";
  }

  public isFiltroVazio(): boolean {
    return !this.filterFormGroup.value.noMovimentacao
      && !this.filterFormGroup.value.vlMovimentacao
      && !this.filterDtMovimentacao.value.dtIni
      && !this.filterDtMovimentacao.value.dtFim
      && !this.filterDtVencimento.value.dtIni
      && !this.filterDtVencimento.value.dtFim
      && !this.filterFormGroup.value.noTipoMovimento;
  }

  public isAcaoDisabled(): boolean {
    return this.isFiltroVazio() && this.movimentos.length === 0;
  }

  private unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = undefined;
    }
  }

  private onResult(res: BespCofmListarControleFinancaMovimento[]) {
    this.movimentos = res ?? [];
  }

  private formatarMoeda(valor: unknown): string {
    return Number(valor ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  private formatarData(valor: string | number | Date | null | undefined): string {
    return this.datePipe.transform(valor, 'dd/MM/yyyy') ?? '';
  }

  private formatarMesAno(valor: string | number | Date | null | undefined): string {
    const mesAno = this.datePipe.transform(valor, 'MMMM yyyy') ?? '';
    return mesAno ? mesAno.charAt(0).toUpperCase() + mesAno.slice(1) : '';
  }
}
